import App from './App.vue';
import {
  createSSRApp,
  createApp as createVueApp,
  App as VueApp,
  defineAsyncComponent,
} from 'vue';

import {
  AemData,
  TechDataVehicle,
  TechDataVariations,
  CabinTypeLabel,
  CabinVariations,
  EngineTypeLabel,
  EngineTypesLabel,
  DrivetrainLabel,
  DrivetrainTypesLabel,
  RoofTypeLabel,
  RoofVariations,
  SizeEngineVariations,
  SizeTypeLabel,
  SizeVariations,
  ProcessedData,
  RenderMode,
  Translations,
} from './types/types';

import { loadVariations, loadVehicles } from './service/getVehicleService';
import { mapSSRContextToAemData } from './utils/aemData';
import { useVehicleSelection } from './composables/useVehicleSelection';
import { loadTranslations } from './service/i18n';
import { getVehicleType } from './utils/vehicleType';

type ApiData = {
  vehicles: TechDataVehicle[];
  variations: TechDataVariations | null;
};

type LoaderData = {
  aemData: AemData;
  processedData: ProcessedData;
  translations: Translations;
  selectedVariant: TechDataVehicle | null;
};

const emptyProcessedData = (): ProcessedData => ({
  sizes: [],
  roofs: [],
  cabins: [],
  batteryCapacities: [],
  engines: [],
  weights: [],
  drivetrains: [],
  vehicles: [],
  variations: null,
});

const AppError = defineAsyncComponent(() => import('./AppError.vue'));

const hasRenderableData = (data?: LoaderData): boolean => {
  const vehiclesCount = data?.processedData?.vehicles?.length ?? 0;
  const variationsCount =
    data?.processedData?.variations?.variations?.length ?? 0;
  return vehiclesCount > 0 && variationsCount > 0;
};

const createApp = async (
  renderMode: RenderMode,
  componentId: string,
  data?: LoaderData,
  error?: { [x: string]: unknown } | null | undefined,
) => {
  let app: VueApp<Element>;

  if (!data || error || !hasRenderableData(data)) {
    app = createVueApp(AppError, {
      componentId,
      title: data?.aemData?.title,
    });
  } else if (import.meta.env.SSR || renderMode !== 'csr') {
    console.groupCollapsed(
      '%cOWVANS TechData SSR',
      'color: #00A3E0; font-weight: 600;',
    );

    console.table({
      Locale: `${data.aemData.country}-${data.aemData.language}`,
      Model: data.aemData.modelSeries,
      RenderMode: renderMode,
      Stage: data.aemData.stage,
    });

    console.groupEnd();
    app = createSSRApp(App, { componentId, data });
  } else {
    app = createVueApp(App, { componentId, data });
  }

  return app;
};

const loader = async (
  context: any,
  isSSR: boolean,
): Promise<LoaderData | null> => {
  const aemData = mapSSRContextToAemData(context, isSSR);

  let apiData: ApiData | null = null;
  let translations: Translations = {};
  let processedData: ProcessedData = emptyProcessedData();

  const fallbackToRawApiData = () => {
    processedData.vehicles = apiData?.vehicles ?? [];
    processedData.variations = apiData?.variations ?? null;
  };

  try {
    apiData = await handleApiCalls(aemData);
  } catch (error) {
    console.error('Failed to fetch API data:', {
      stage: aemData.stage,
      country: aemData.country,
      language: aemData.language,
      modelSeries: aemData.modelSeries,
      error,
    });
    return {
      aemData,
      processedData: emptyProcessedData(),
      translations: {},
      selectedVariant: null,
    };
  }

  try {
    translations = await loadTranslations(aemData.language, aemData.country);
  } catch (error) {
    console.error('Failed to fetch translations:', error);
    translations = {};
  }

  try {
    const maybeProcessed = processAPIData({
      vehicles: apiData.vehicles,
      variations: apiData.variations,
    });

    if (maybeProcessed) {
      processedData = maybeProcessed;
    } else {
      console.warn(
        'Processing API data returned null/undefined, using fallback.',
      );
      fallbackToRawApiData();
    }
  } catch (error) {
    console.error('Error processing API data:', error);
    fallbackToRawApiData();
  }

  // Calculate selected variant using useVehicleSelection
  const selectedVariant =
    processedData.vehicles.length > 0 && processedData.variations
      ? selectInitialVariant(processedData.vehicles, processedData.variations)
      : null;

  return {
    aemData,
    processedData,
    translations,
    selectedVariant: selectedVariant ?? null,
  };
};

const handleApiCalls = async (aemData: AemData) => {
  try {
    const [vehicles, variations] = await Promise.all([
      loadVehicles(aemData),
      loadVariations(aemData),
    ]);

    return { vehicles, variations };
  } catch (e) {
    console.error('Failed to load tech data', {
      stage: aemData.stage,
      country: aemData.country,
      language: aemData.language,
      modelSeries: aemData.modelSeries,
      error: e,
    });
    throw e;
  }
};

const selectInitialVariant = (
  vehicles: TechDataVehicle[],
  variations: TechDataVariations,
): TechDataVehicle | undefined => {
  if (!vehicles.length) return undefined;
  if (!variations?.variations?.length) return vehicles[0];

  const vehicleType = getVehicleType(variations);
  if (!vehicleType || !variations.variations?.length) {
    console.warn('No vehicle type or variations available');
    return vehicles[0];
  }

  try {
    // Initialize useVehicleSelection to handle initial variant selection
    const { setVehiclePropertiesBySize, vehicle } = useVehicleSelection(
      vehicleType,
      variations,
      vehicles,
    );

    // Set the initial variation
    const firstVariation = variations.variations[0];
    setVehiclePropertiesBySize(firstVariation);

    // Return the selected vehicle
    return vehicle.value || vehicles[0];
  } catch (error) {
    console.warn('Error selecting initial variant:', error);
    return vehicles[0]; // Fallback to first vehicle if error occurs
  }
};

const getVehicleData = (response: TechDataVariations | null) => {
  if (!response?.variations?.length) return null;

  const variations = response.variations;

  const drivetrainsSet = new Set<DrivetrainLabel>();
  const engineTypesSet = new Set<EngineTypeLabel>();
  const batteryCapacitiesSet = new Set<number>();
  let weightsMax: number[] = [];
  let roofTypes: RoofTypeLabel[] = [];
  if ((variations[0] as RoofVariations).roofHeightTypes) {
    // extract values
    (variations as RoofVariations[]).forEach((roofVariant) => {
      roofTypes.push(
        ...roofVariant.roofHeightTypes.map((e) => e.roofHeightType),
      );
      roofVariant.roofHeightTypes.map((roofType) => {
        weightsMax.push(...roofType.weightsMax.map((e) => e.weightMax));

        roofType.weightsMax.map((e) => {
          e.drivetrainTypes.map((e: DrivetrainTypesLabel) => {
            drivetrainsSet.add(e.drivetrainType);
            e.engineTypes.map((e: EngineTypesLabel) => {
              engineTypesSet.add(e.engineType);
              Array.isArray(e.batteryCapacities) &&
                e.batteryCapacities.forEach((e) => {
                  batteryCapacitiesSet.add(e);
                });
            });
          });
        });
      });
    });
    // remove duplicates and sort
    roofTypes = Array.from(
      new Set(roofTypes.sort((e) => Object.keys(RoofTypeLabel).indexOf(e))),
    );
  }

  let cabinTypes: CabinTypeLabel[] = [];
  if ((variations[0] as CabinVariations).cabinTypes) {
    // extract values
    (variations as CabinVariations[]).forEach((cabinVariant) => {
      cabinTypes.push(...cabinVariant.cabinTypes.map((e) => e.cabinType));
      cabinVariant.cabinTypes.map((cabinType) => {
        weightsMax.push(...cabinType.weightsMax.map((e) => e.weightMax));

        cabinType.weightsMax.map((e) => {
          e.drivetrainTypes.map((e: DrivetrainTypesLabel) => {
            drivetrainsSet.add(e.drivetrainType);
            e.engineTypes.map((e: EngineTypesLabel) => {
              engineTypesSet.add(e.engineType);
              Array.isArray(e.batteryCapacities) &&
                e.batteryCapacities.forEach((e) => {
                  batteryCapacitiesSet.add(e);
                });
            });
          });
        });
      });
    });
    // remove duplicates and sort
    cabinTypes = Array.from(
      new Set(cabinTypes.sort((e) => Object.keys(CabinTypeLabel).indexOf(e))),
    );
  }

  if ((variations[0] as SizeEngineVariations).engineTypes) {
    // extract values
    (variations as SizeEngineVariations[]).forEach((sizeEngineVariant) => {
      sizeEngineVariant.engineTypes.map((e) => {
        engineTypesSet.add(e.engineType);
        Array.isArray(e.batteryCapacities) &&
          e.batteryCapacities.forEach((e) => {
            batteryCapacitiesSet.add(e);
          });
      });
    });
  }

  if ((variations[0] as SizeVariations).weightsMax) {
    (variations as SizeVariations[]).forEach((sizeVariant) => {
      weightsMax.push(...sizeVariant.weightsMax.map((e) => e.weightMax));
      sizeVariant.weightsMax.map((e) => {
        e.drivetrainTypes.map((e: DrivetrainTypesLabel) => {
          drivetrainsSet.add(e.drivetrainType);
          e.engineTypes.map((e: EngineTypesLabel) => {
            engineTypesSet.add(e.engineType);
            Array.isArray(e.batteryCapacities) &&
              e.batteryCapacities.forEach((e) => {
                batteryCapacitiesSet.add(e);
              });
          });
        });
      });
    });
  }

  // remove duplicates and sort
  weightsMax = Array.from(new Set(weightsMax.sort()));
  const drivetrain = Array.from(drivetrainsSet);
  const engineTypes = Array.from(engineTypesSet);

  const sizes: SizeTypeLabel[] = Array.from(
    new Set(
      variations
        .map((it: { sizeType: any }) => it.sizeType)
        .sort((e: string) => Object.keys(SizeTypeLabel).indexOf(e)),
    ),
  );
  const batteryCapacities = Array.from(batteryCapacitiesSet).sort();

  return {
    sizes,
    roofTypes,
    cabinTypes,
    drivetrain,
    engineTypes,
    batteryCapacities,
    weightsMax,
    response,
  };
};

const processAPIData = (data: {
  vehicles: TechDataVehicle[];
  variations: TechDataVariations | null;
}): ProcessedData | null => {
  const vehicleData = getVehicleData(data.variations);

  if (!data.vehicles?.length) return null;

  return {
    vehicles: data.vehicles,
    variations: data.variations,
    sizes: vehicleData?.sizes ?? [],
    roofs: vehicleData?.roofTypes ?? [],
    cabins: vehicleData?.cabinTypes ?? [],
    batteryCapacities: vehicleData?.batteryCapacities ?? [],
    engines: vehicleData?.engineTypes ?? [],
    weights: vehicleData?.weightsMax ?? [],
    drivetrains: vehicleData?.drivetrain ?? [],
  };
};

export default {
  createApp,
  loader,
};
